/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Playback error codes that are NOT derived from a Shaka error category.
 *
 * Codes mirror kaveri-web-app's `ErrorTypes.*` verbatim so the same code
 * identifies the same condition across both clients, and so support can look
 * a code up in the shared error dictionary (kaveri's `errors.xml`). The Shaka
 * category codes live separately in {@link PlayerErrorCode}.
 *
 * This module is a catalogue only — it carries no messages, titles or
 * surface/priority decisions. Surface routing lives in `ErrorDisplayRouting`;
 * localized text is owned by whichever producer raises the error.
 */

/**
 * Restriction errors — business rules that block playback of otherwise
 * playable content. All surface as an OSD.
 */
export const RestrictionErrorCode = {
  /** Client IP is outside the partner's allowed range (blacklisted). */
  BLACKLISTED: "C138",
  /** Streaming blocked because the device is away from its home network. */
  OUT_OF_HOME: "V801",
  /** Streaming blocked outside the permitted regional viewing area. */
  OUT_OF_REGION: "V804",
  /** Streaming not permitted on this device type by the content provider. */
  DEVICE_RESTRICTED: "V414",
  /** The channel requires a subscription the account does not hold. */
  UNSUBSCRIBED: "V56",
  /** Device location could not be determined — location service unreachable. */
  LOCATION_RESTRICTION: "V811"
} as const;

/**
 * Session errors — failures reported by session-create / keep-alive.
 * All surface as a full-screen dialog.
 */
export const SessionErrorCode = {
  /** Session aborted: syntax/semantic error, bad configuration, deprecated,
   *  external error, or host unavailable for live. */
  GENERIC_LIVE_ERROR: "V851",
  /** external error, or host unavailable for vod. */
  GENERIC_VOD_ERROR: "V852",
  /** external error, or host unavailable for SoCu. */
  GENERIC_SOCU_ERROR: "V853",
  /** external error, or host unavailable for nDVR. */
  GENERIC_NDVR_ERROR: "V854",
  /** Session aborted due to an internal service error. */
  INTERNAL_ERROR: "V138",
  /** The account or device is not authenticated. */
  NOT_AUTHENTICATED: "V403",
  /** The device is not registered/provisioned for streaming. */
  NOT_REGISTERED: "V406",
  /** Concurrent-stream cap for the account reached. */
  TOO_MANY_STREAMS: "V407",
  /** Concurrent-stream cap for this specific resource reached. */
  TOO_MANY_STREAMS_FOR_RESOURCE: "V408",
  /** The streaming session for the app has expired. */
  AUTHENTICATION_EXPIRED: "V409",
  /** Not authorized — a subscription is required for this content. */
  NOT_AUTHORIZED: "V410",
  /** Session-create returned no session. */
  NO_SESSION: "V902",
  /** Unrecognised session-create failure. */
  UNKNOWN_ERROR: "V603",
  /** nDVR variant of NOT_AUTHORIZED / NOT_AUTHORIZED_DEVICE_TYPE. */
  NDVR_NOT_AUTHORIZED: "V400"
} as const;

/**
 * Playback-setup errors — failures preparing playback, before or around the
 * player itself. All surface as a full-screen dialog.
 */
export const PlaybackSetupErrorCode = {
  /** Playback requested before the playback models were ready. */
  NOT_READY_FOR_PLAYBACK: "V117",
  /** Airing data fetch failed, so adult/rating checks cannot complete. */
  AIRING_DATA_FAILED: "V139",
  /** Required device configuration is missing. */
  DEVICE_MISSING_CONFIG: "V904",
  /** Session response carried no playlist URL. */
  PLAYLIST_URI_MISSING: "V908",
  /** Video-provider configuration is invalid for this channel/device. */
  VP_CONFIGURATIONS_MISCONFIGURATION: "V909",
  /** Video-provider configuration response was empty. */
  VP_CONFIGURATIONS_EMPTY_RESPONSE: "V934",
  /** The app does not support playback of this content type. */
  UNSUPPORTED_CONTENT: "V950",
  /** A grace period covering an unreachable service has elapsed. */
  GRACE_PERIOD_ENDED: "C718"
} as const;

/**
 * Connectivity errors.
 */
export const ConnectivityErrorCode = {
  /** The device has no network/internet connection. */
  NO_NETWORK: "C145"
} as const;

/**
 * Cross-cutting fallback for a failure with no more specific canonical code.
 * Not tied to any one producer (session, metadata, etc.) — use wherever a
 * catalogued code doesn't exist yet for the failure. Surfaces as a dialog.
 *
 * Sourced from `unity-android`'s `v-error-code-implementation.md` (`V555`,
 * `EXO_SOURCE_EXCEPTION`), where it is documented as a player source-exception
 * catch-all; repurposed here as a generic cross-layer fallback.
 */
export const GenericErrorCode = {
  /** Catch-all for any failure with no more specific canonical code. */
  UNKNOWN_ERROR: "V555"
} as const;
